import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export function AuthVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyToken, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    // Already authenticated - redirect
    if (isAuthenticated) {
      navigate('/');
      return;
    }

    // No token provided
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided');
      return;
    }

    // Verify the token
    const verify = async () => {
      try {
        await verifyToken(token);
        setStatus('success');
        // Redirect to home after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } catch (error) {
        setStatus('error');
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to verify token'
        );
      }
    };

    verify();
  }, [token, verifyToken, navigate, isAuthenticated]);

  return (
    <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
      <div className="w-full max-w-md text-center">
        {status === 'verifying' && (
          <div>
            <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="text-xl font-semibold text-white mb-2">
              Verifying your magic link...
            </h1>
            <p className="text-zinc-400">Please wait a moment</p>
          </div>
        )}

        {status === 'success' && (
          <div>
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">
              Successfully signed in!
            </h1>
            <p className="text-zinc-400 mb-6">Redirecting you to the home page...</p>
            <Link
              to="/"
              className="text-orange-500 hover:text-orange-400 transition-colors"
            >
              Click here if you're not redirected
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div>
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-white mb-2">
              Verification failed
            </h1>
            <p className="text-red-400 mb-6">{errorMessage}</p>
            <div className="space-y-4">
              <Link
                to="/login"
                className="block py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
              >
                Request a new magic link
              </Link>
              <Link
                to="/"
                className="block text-sm text-zinc-400 hover:text-white transition-colors"
              >
                Back to home
              </Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
